import { Redirect, Stack } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { supabase } from '@sixam/supabase/client';
import { TERMS_VERSION } from '@sixam/shared/community-terms';

import { LiveCheckInProvider } from '@/components/events/live-check-in-entry';
import { TermsGate } from '@/components/moderation/terms-gate';
import { useSession } from '@/components/session-provider';
import { acceptedCurrentTermsThisSession } from '@/lib/community-terms-client';

export default function AuthenticatedLayout() {
  const { isLoading, profile, session } = useSession();
  // Set locally the moment the gate is satisfied, so the app opens without
  // waiting for the profile to be refetched.
  const [justAcceptedTerms, setJustAcceptedTerms] = React.useState(false);

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-background">
        <ActivityIndicator />
      </View>
    );
  }

  if (!session) {
    return <Redirect href="/(public)/sign-in" />;
  }

  /*
    An ejected member is stopped here rather than merely hidden from others.
    Guideline 1.2 asks that the member who posted objectionable content is
    ejected, and an account that can still sign in and post has not been.
  */
  if (profile?.suspended_at) {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-background p-8">
        <Text variant="h2" className="text-center">
          Account suspended
        </Text>
        <Text className="text-center">
          This account was suspended for breaching the community terms. If you think that is a
          mistake, contact the club organisers.
        </Text>
        <Button onPress={() => void supabase.auth.signOut()} variant="secondary">
          <Text>Sign out</Text>
        </Button>
      </View>
    );
  }

  /*
    Everyone passes through here, which is the only place that catches members
    who already had an account. Sign-up's checkbox covers new registrations
    only, and App Review signs in rather than registering.
  */
  const acceptedCurrentTerms =
    justAcceptedTerms ||
    (session ? acceptedCurrentTermsThisSession(session.user.id) : false) ||
    (profile?.terms_accepted_at != null && profile.terms_version === TERMS_VERSION);
  if (profile && !acceptedCurrentTerms) {
    return <TermsGate onAccepted={() => setJustAcceptedTerms(true)} />;
  }

  return (
    <LiveCheckInProvider>
      <Stack screenOptions={{ headerShown: false }}>
        <Stack.Screen name="app" />
        <Stack.Screen name="admin" />
      </Stack>
    </LiveCheckInProvider>
  );
}
